// Copyright (c) 2019-24 Stax·ai, Inc.
// See LICENSE.md file in project root directory

import React from 'react';
import { toast } from 'react-toastify';
import Scrollbars from 'react-custom-scrollbars';
import { withRouter } from 'react-router-dom';

import api from '../../api';

// Components
import Page from '../MyStacks/Document/Page';
import ContextMenu from '../ContextMenu';

// Parent/base class
import Document from '../MyStacks/Document';

// Constants
import { RENDER_COUNT, ASPECT_RATIO, PAGE_MARGIN } from '../MyStacks/Document/constants';

// Styles
import '../../styles/public.css';
import formatDate from '../../utilities/formatDate';

class PublicDocument extends Document {

    state = {

        // Blob paths for downloaded pages
        pages: {},

        // Selection
        selection: [],

        // Zoom factor
        zoom: 1,

        // Dynamic loading
        dynamicLoad: false,
        startIdx: 0,
        endIdx: RENDER_COUNT-1,
        aspects: [],

        // Fillable form
        fillable: null
    }

    // API URL
    url = '/document/public'

    tools = [
        {
            name: "Fit to screen",
            icon: "fit-screen.png",
            action: () => this.docZoomHandler(1)
        },
        {
            name: "Fit page width",
            icon: "fit-width.png",
            action: () => this.docZoomHandler((this.refs.document.clientWidth - 100) / (0.78 * this.refs.document.clientHeight - 60))
        },
        {
            name: "Zoom in",
            icon: "zoom-in.png",
            action: () => this.docZoomHandler(this.state.zoom * 1.25 )
        },
        {
            name: "Zoom out",
            icon: "zoom-out.png",
            action: () => this.docZoomHandler(this.state.zoom / 1.25)
        }
    ]

    componentDidMount = async () => {
        await this.loadDocument(this.props.match.params.documentId, this.props.auth);

        // Setup keyboard shortcuts
        window.addEventListener("keydown", (event) => {
            if (event.key === "c" && event.ctrlKey)
                this.copy();

            if (event.key === "Escape")
                this.setState({ selection: [] });
        });

        // Fillable document?
        setTimeout(async () => {
            const query = new URLSearchParams(this.props.location.search);
            const fill = query.get('fill');
            if (fill && fill === "true") {
                let fields = this.props.document && this.props.document.fillable && this.props.document.fillable.enabled && this.props.document.fillable.fields ? this.props.document.fillable.fields : [];
                
                fields = fields.filter((field) => 'x' in field && 'y' in field && field.width && field.height && 'page' in field);

                // Order fields by page and y-axis
                fields.sort((a,b) => {
                    if (a.dataType === "signature" && b.dataType !== "signature") return 1;
                    if (b.dataType === "signature" && a.dataType !== "signature") return -1;
                    if (a.page === b.page)
                        return a.y > b.y ? 1 : -1;
                    return a.page > b.page ? 1 : -1;
                });

                const metadata = {};

                // Are metadata fields provided in URL?
                const q = new URLSearchParams(this.props.location.search);
                for (const param of q.keys()) {
                    if (!param.startsWith("set:")) continue; // Not a meta parameter
                    metadata[param.substring(4)] = q.get(param);
                }

                await this.setState({
                    fillable: {
                        fields,
                        signatures: [],
                        metadata // Need to convert to actual metadata format after
                    }
                });

                setTimeout(() => this.nextField(0), 250);
            }
        }, 250);

        // Refs
        this.centerPage = React.createRef();
    }

    componentWillReceiveProps = async (next) => {
        // Reload (correct) document if updated
        if (next.match.params.documentId !== this.props.match.params.documentId || next.auth !== this.props.auth)
            await this.loadDocument(next.match.params.documentId, next.auth);
    }

    componentWillUnmount = async () => {
        this.unloadPageData();
        this.cancelToken.cancel("Document unmount");
        clearTimeout(this.scrollTimer);
    }

    focusField = () => {
        if (!this.focusOnScroll) return;
        this.focusOnScroll.focus();
        this.focusOnScroll = null;
    }

    nextField = async (stage, override=false) => {
        if (!this.state.fillable || !this.state.fillable.fields) return;
        const n = this.state.fillable.fields.length;
        if (stage >= n) {
            this.setState(prev => ({
                ...prev,
                fillable: {
                    ...prev.fillable,
                    stage: -1
                }
            }));
            
            return this.props.submit(this.state.fillable.metadata, this.state.fillable.signatures);
        }

        const field = this.state.fillable.fields[stage];
        if (!field) return toast.error("There is an error with the form. Please contact the person who sent you this document.");

        await this.setState(prev => ({
            ...prev,
            fillable: {
                ...prev.fillable,
                stage
            }
        }));

        // Scroll to page with stage
        if (this.scrollTimer) clearTimeout(this.scrollTimer); // Clear previous timer
        this.scrollTimer = setTimeout(() => {
            const fref = document.getElementById("document-fillable-" + String(stage));
            if (fref) {
                const rect = fref.getBoundingClientRect();
                const top = this.refs.documentViewerScroll.getScrollTop() + rect.top - 300;
                this.focusOnScroll = fref;
                this.refs.documentViewerScroll.view.scroll({
                    top,
                    behavior: 'smooth'
                });
                if (Math.abs(this.refs.documentViewerScroll.getScrollTop() - top) < this.refs.documentViewerScroll.getClientHeight() * 1.2)
                    setTimeout(this.focusField, 200);
            }
        }, 200);

        let metadata = { ...this.state.fillable.metadata };

        // Skip if next field value already exists in meta
        if (metadata[field.key] && !override)
            return this.nextField(stage+1);

        // If field type is today, set value to current Date automatically
        if (field.dataType === "today") {
            metadata[field.key] = formatDate(new Date(), false, true);
            await this.setState(prev => ({
                ...prev,
                fillable: {
                    ...prev.fillable,
                    metadata
                }
            }));
            return this.nextField(stage+1);
        }
    }

    setFieldValue = async (key, value) => {
        const stage = this.state.fillable.stage;
        let metadata = { ...this.state.fillable.metadata };
        metadata[key] = value;
        await this.setState(prev => ({
            ...prev,
            fillable: {
                ...prev.fillable,
                metadata
            }
        }));

        this.nextField(stage + 1);
    }

    setSignature = async (data) => {
        const stage = this.state.fillable.stage;
        const { page, x, y, width, height } = this.state.fillable.fields[stage];
        await this.setState(prev => ({
            ...prev,
            fillable: {
                ...prev.fillable,
                signatures: [
                    ...prev.fillable.signatures,
                    {
                        fieldIdx: stage,
                        page,
                        x,
                        y,
                        width,
                        height,
                        data
                    }
                ]
            }
        }));
        this.nextField(stage + 1);
    }

    getContextMenuOptions = () => {
       
        const doc = this.props.document;
        let options = [];

        if (this.state.selection.length > 0)
            options.push(
                {
                    label: 'Copy selection',
                    action: () => this.copy()
                }, {
                    label: 'Find all matches',
                    action: () => this.props.search(this.state.selection.map(w => w.text).join(" "))
                }
            );

        options.push(
            {
                label: "View in browser",
                action: () => api.open(this.url + '/download?docId=' + doc._id)
            },
            {
                label: "Download",
                action: this.props.downloadDocument
            },
            {
                label: "Print",
                action: this.props.printDocument
            }
        );

        return options;
        }

    render = () => {

        // const user = this.props.user;
        const startIdx = this.state.startIdx;
        const endIdx = this.state.endIdx;
        const doc = this.props.document ? this.props.document : { _id: null, pages: [], words: [] };

        const renderedPages = this.range(startIdx, endIdx);

        const matches = this.props.matches ? this.props.matches : {
            count : 0,
            index: 0,
            words: []
        };
        
        const docLen = doc.pages.length;

        // Find sizes of blank space
        var topHeight = 0;
        var botHeight = 0;

        // If the css is ever updated, this will need to be updated as well. Sorry.
        for (let i=0; i<docLen; i++) {
            let pageHeight = 0;
            if (i < this.state.aspects.length)
                pageHeight = (.78*window.innerHeight - 60) * this.state.aspects[i] + PAGE_MARGIN;
            else
                pageHeight = (.78*window.innerHeight - 60) * ASPECT_RATIO + PAGE_MARGIN;

            if (i < startIdx)
                topHeight += pageHeight;
            else if (i > endIdx)
                botHeight += pageHeight;
        }
        

        return (<div className={"document-modal-public"} ref="document">
            <Scrollbars
                ref="documentViewerScroll"
                onScroll={this.onDocScroll}
                onScrollStop={this.focusField}
                renderTrackVertical={props => <div className="scrollbar-track-v" {...props} />}
                renderTrackHorizontal={props => <div className="scrollbar-track-h" {...props} />}
                renderThumbVertical={props => <div className="scrollbar-thumb-v" {...props} />}
                renderThumbHorizontal={props => <div className="scrollbar-thumb-h" {...props} />}
            >

                {<div
                    ref={this.topBlank}
                    className="blankPage"
                    style={{
                        width: "calc(" + this.state.zoom + " * (78vh - 60px))",
                        height: topHeight + "px",
                    }}
                />}

                {renderedPages.map(page => <Page
                    onContextMenu={(e) => this.openMenu(e,page)}
                    key={page}
                    pageIdx={page}
                    pageKey={page}
                    aspectRatio={this.state.aspects[page]}
                    pageData={this.state.pages[page]}
                    rotation={doc.rotations ? doc.rotations[page] : 0}
                    inputs={[]}
                    user={this.props.user}
                    users={[]}
                    words={doc && doc.words ? doc.words.filter(w => w.page === page) : []}
                    zoom={this.state.zoom}
                    tool={"Select"}
                    canvas={doc.canvas ? doc.canvas[page] : ""}
                    selection={this.state.selection.filter(w => w ? w.page === page : false)}
                    select={selection => this.setState({ selection })}
                    query={this.props.query}
                    matches={matches.words.filter(m => m.page === page)}
                    matchIdx={matches.index}
                    modal={this.props.modal}

                    // Fillable form?
                    stage={this.state.fillable && this.state.fillable.stage ? this.state.fillable.stage : 0}
                    fillable={this.state.fillable && this.state.fillable.fields ? this.state.fillable.fields : []}
                    metadata={this.state.fillable && this.state.fillable.metadata ? this.state.fillable.metadata : {}}
                    signatures={this.state.fillable && this.state.fillable.signatures ? this.state.fillable.signatures : []}
                    fill={this.setFieldValue}
                    sign={this.setSignature}
                    jumpTo={this.nextField}

                />)}

                {<div
                    ref={this.bottomBlank}
                    className="blankPage"
                    style={{
                        width: "calc(" + this.state.zoom + " * (78vh - 60px))",
                        height: botHeight + "px",
                    }}
                />}

            </Scrollbars>

            {/* Limited toolbar */}
            <div
                className={"document-toolbar document-toolbar-public" + (this.state.showToolbar || (new URLSearchParams(this.props.location.search).get('controls') === 'show') ? " document-toolbar-show" : "")}
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                }}
            >
                {this.renderToolbar()}
            </div>

            {/* Search result/match counter */}
            {!this.props.searching && matches.count > 0 && <div className="document-search-counter document-search-counter-nometa">
                <div
                    className="document-search-counter-button"
                    onClick={() => this.gotoMatch(0)}
                >«</div>
                <div
                    className="document-search-counter-button"
                    onClick={() => this.gotoMatch(matches.index > 0 ? matches.index-1 : 0)}
                >‹</div>
                <div
                    className="document-search-counter-value"
                >{matches.index+1} of {matches.count}</div>
                <div
                    className="document-search-counter-button"
                    onClick={() => this.gotoMatch(matches.index < matches.count-1 ? matches.index+1 : matches.count-1)}
                >›</div>
                <div
                    className="document-search-counter-button"
                    onClick={() => this.gotoMatch(matches.count-1)}
                >»</div>
            </div>}

            {/* Document page counter */}
            <div
                className="document-page-counter"
                style={{ opacity: this.state.showPageCounter ? 1 : 0 }}
            >Page {this.state.currentPage+1} of {doc.pages.length}</div>

            <ContextMenu
                load={(open,close) => {
                    this.openMenu = open;
                    this.closeMenu = close;
                }}
                options={this.getContextMenuOptions()}
            />

        </div>);

    }

};

export default withRouter(PublicDocument);